import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2 } from "lucide-react";
import ChatInput from "./ChatInput";

// Minimal typographic styling for assistant markdown, scoped to the bubble.
// These are descendant utilities rather than the @tailwindcss/typography
// plugin's class, which applies its own text colours that would then have to
// be overridden element by element; inheriting currentColor instead keeps the
// bubble's look correct in both light and dark mode for free. Tailwind
// preflight strips list markers and heading sizes, so those are restored here.
// Avoid writing that plugin's class name anywhere in this file: Tailwind
// scans comments too, and the bare word pulls its whole stylesheet into the
// bundle.
const MARKDOWN_CLASSES = [
  "text-base leading-relaxed",
  "[&_p]:my-2",
  "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5",
  "[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5",
  "[&_li]:my-1 [&_li>ul]:my-1 [&_li>ol]:my-1",
  // Katal's global stylesheet sets `.katal ul li { color: #002f36 }`, and
  // `.katal` wraps the whole app. At (0,1,2) it outranks a plain `[&_li]:`
  // utility, which is why list text came out near-black on the dark bubble
  // while paragraphs were fine. Restoring inheritance (rather than hardcoding
  // a colour) lets both modes follow the bubble. `.katal pre { color: #fff }`
  // is the same problem mirrored: invisible in light mode.
  "[&_li]:!text-inherit [&_ul]:!text-inherit [&_ol]:!text-inherit [&_pre]:!text-inherit",
  "[&_h1]:mt-3 [&_h1]:mb-1 [&_h1]:text-lg [&_h1]:font-semibold",
  "[&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:text-base [&_h2]:font-semibold",
  "[&_h3]:mt-3 [&_h3]:mb-1 [&_h3]:text-base [&_h3]:font-semibold",
  "[&_h4]:mt-3 [&_h4]:mb-1 [&_h4]:text-base [&_h4]:font-semibold",
  "[&_strong]:font-semibold [&_em]:italic",
  "[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-gray-400/60 [&_blockquote]:pl-3 [&_blockquote]:italic dark:[&_blockquote]:border-gray-400/40",
  "[&_hr]:my-3 [&_hr]:border-gray-400/40",
  // `.katal a` forces a teal that is too dim on the dark bubble, so links get
  // an explicit colour per mode. They keep their underline either way.
  "[&_a]:!text-blue-700 dark:[&_a]:!text-blue-300",
  "[&_code]:rounded [&_code]:bg-black/10 [&_code]:px-1 [&_code]:text-[0.9em] dark:[&_code]:bg-white/10",
  "[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-black/10 [&_pre]:p-3 dark:[&_pre]:bg-white/10",
  "[&_pre_code]:bg-transparent [&_pre_code]:px-0",
  "[&_table]:my-2 [&_table]:w-full [&_table]:text-left",
  "[&_th]:border [&_th]:border-gray-400/40 [&_th]:px-2 [&_th]:py-1",
  "[&_td]:border [&_td]:border-gray-400/40 [&_td]:px-2 [&_td]:py-1",
  "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
].join(" ");

const MARKDOWN_COMPONENTS = {
  a: ({ node, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer" className="underline" />
  ),
};

const AnimatedChatUI = ({
  messages,
  loading,
  streaming,
  loadingMessage,
  userInput,
  setUserInput,
  handleSend,
  scrollRef,
}) => {
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        // While the response is being revealed this runs on every tick, and
        // smooth scrolling at that rate fights itself, so snap instead.
        behavior: streaming ? "auto" : "smooth",
      });
    }
  }, [messages, loading, streaming]);

  return (
    /*
      Shares layoutId "chatShell" with the input card in WelcomeScreen, so the
      input bar grows into this card. Keep the surface classes in sync there.
    */
    <motion.div
      layoutId="chatShell"
      transition={{ layout: { duration: 0.45, ease: "easeInOut" } }}
      className="col-start-1 row-start-1 w-full max-w-[90vw] md:max-w-[1400px] h-[75vh] p-4 md:p-6 lg:p-8 flex flex-col bg-white/70 dark:bg-gray-800/80 shadow-lg rounded-2xl border border-gray-100/30 dark:border-gray-700/50 backdrop-blur-sm"
    >
      {/* Delayed so the text appears once the card has finished growing. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25, delay: 0.4 }}
        className="flex-1 min-h-0 overflow-y-auto p-2 rounded-lg bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm"
      >
        <div className="flex flex-col space-y-4 p-2 rounded-lg min-h-full">
          {messages.map((msg, i) => {
            const isUser = msg.role === "user";
            const isStreamingMessage =
              streaming && !isUser && i === messages.length - 1;

            return (
              <div
                key={i}
                className={`flex w-full my-4 ${
                  isUser ? "justify-end" : "justify-start"
                }`}
              >
                {/* Message bubble with role label inside */}
                <div
                  className={`p-4 max-w-[75%] rounded-lg shadow-md border ${
                    isUser
                      ? "bg-blue-200/90 text-gray-800 dark:bg-blue-700/80 dark:text-white border-blue-300/50 dark:border-blue-600/30 backdrop-blur-sm"
                      : "bg-gray-200/80 text-gray-900 dark:bg-gray-700/80 dark:text-white border-gray-300/50 dark:border-gray-600/50 backdrop-blur-sm"
                  }`}
                >
                  <div className="font-medium mb-2">
                    {isUser ? "You:" : "CoachAI:"}
                  </div>
                  {isUser ? (
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                  ) : (
                    <>
                      {/* While streaming, let the trailing paragraph go inline
                          so the cursor sits right after the last word. */}
                      <div
                        className={`${MARKDOWN_CLASSES} ${
                          isStreamingMessage
                            ? "[&>p:last-child]:inline [&>p:last-child]:mb-0"
                            : ""
                        }`}
                      >
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={MARKDOWN_COMPONENTS}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                      {isStreamingMessage && (
                        <span className="inline-block ml-1 animate-pulse">▌</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {loading && loadingMessage && (
            <div className="flex justify-start">
              <div className="p-4 bg-gray-200/80 text-gray-900 dark:bg-gray-700/80 dark:text-white rounded-lg animate-pulse flex items-center gap-2 shadow-md border border-gray-300/50 dark:border-gray-600/50 backdrop-blur-sm">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                {loadingMessage}
              </div>
            </div>
          )}

          {/* Keep the original scrollRef for compatibility */}
          <div ref={scrollRef} style={{ display: 'none' }} />

          {/* Add our new scroll anchor at the end of messages */}
          <div ref={messagesEndRef} />
        </div>
      </motion.div>

      <ChatInput
        value={userInput}
        onChange={setUserInput}
        onSend={handleSend}
        placeholder="Ask me a question..."
        autoFocus
        disabled={loading}
        className="mt-4"
      />
    </motion.div>
  );
};

export default AnimatedChatUI;
